import { useState, useEffect } from 'react';

import {
  DotLoader,
  Header,
  IconFlag,
  PrimaryButton,
  Space,
  Spacer,
  Text,
  TextButton,
} from '@procurify/ui'
import { useIntl } from 'react-intl'

import { useFormatMedia } from '@webapp/core/hooks/useFormatMedia'
import {
  type ExportStatus,
  ExportStatuses,
} from '@webapp/models/enums/ExportStatus'
import { ExportCSVChannelManager } from '@webapp/services/ExportCSVChannelManager/ExportCSVChannelManager.service'
import exportHeaderImage from './images/modal.exporting.header.png'
import messages from './messages'
import { ExporterProcessingStatusStyled } from './styles/ExporterProcessingStatusStyled'

interface ExportProgress {
  totalRows: number
  processedRows: number
}

export interface IExporterProcessingStatusProps {
  exportStatusUrl?: string
  exportFilters?: {
    label: string
    value: string
  }[]
  subscribe?: {
    endpoint: string
    event: string
    channel: string
    initialProgress: ExportProgress
  }
}

export const ExporterProcessingStatus = ({
  exportStatusUrl,
  exportFilters = [],
  subscribe,
}: IExporterProcessingStatusProps) => {
  const intl = useIntl()
  const formatMedia = useFormatMedia()

  const [processState, setProcessState] = useState<ExportStatus>(
    ExportStatuses.PROCESSING
  )
  const [progress, setProgress] = useState<ExportProgress>(
    subscribe?.initialProgress || {
      processedRows: 0,
      totalRows: 0,
    }
  )
  const [csvUrl, setCsvUrl] = useState<string>(null)

  useEffect(() => {
    if (!subscribe) return

    const ExportChannelManager = new ExportCSVChannelManager({
      endpoint: subscribe.endpoint,
      channel: subscribe.channel,
      event: subscribe.event,
    })

    ExportChannelManager.onProgress = ({ processed, total }) => {
      setProcessState(ExportStatuses.PROCESSING)

      setProgress({
        totalRows: total,
        processedRows: processed,
      })
    }

    ExportChannelManager.onComplete = (file) => {
      setProcessState(ExportStatuses.COMPLETED)

      setCsvUrl(formatMedia(file.uuid))
    }

    ExportChannelManager.onError = () => {
      setProcessState(ExportStatuses.ERROR)
    }

    return () => {
      ExportChannelManager.destroy()
    }
  }, [])

  const renderDownloadButton = () => {
    if (processState === ExportStatuses.PROCESSING) {
      return (
        <PrimaryButton disabled iconComponent={<DotLoader />}>
          {intl.formatMessage(messages.preparingButtonText)}
        </PrimaryButton>
      )
    } else if (processState === ExportStatuses.COMPLETED) {
      return (
        <a download href={csvUrl}>
          <PrimaryButton iconComponent={<IconFlag color='white' />}>
            {intl.formatMessage(messages.downloadButtonText)}
          </PrimaryButton>
        </a>
      )
    } else if (processState === ExportStatuses.ERROR) {
      return (
        <PrimaryButton disabled>
          {intl.formatMessage(messages.exportErrButtonText)}
        </PrimaryButton>
      )
    }
  }

  const renderProcessStatus = () => {
    if (
      processState === ExportStatuses.PROCESSING &&
      progress.processedRows === 0
    ) {
      return intl.formatMessage(messages.exportStatusQueueing)
    } else if (
      (processState === ExportStatuses.PROCESSING ||
        processState === ExportStatuses.COMPLETED) &&
      progress.processedRows > 0
    ) {
      const percentage = Math.ceil(
        (progress.processedRows / progress.totalRows) * 100
      )
      return `${intl.formatMessage(messages.exportStatusCompleted)}: ${
        progress.processedRows
      }/${progress.totalRows} ${percentage}%`
    } else if (processState === ExportStatuses.ERROR) {
      // This is the only error message from the migration
      return intl.formatMessage(messages.exportStatusError)
    } else {
      return null
    }
  }

  const renderProcessingStateButtons = () => {
    const ViewCSVButton = subscribe ? TextButton : PrimaryButton
    return (
      <Space direction='horizontal' justify='center' align='center'>
        {exportStatusUrl && (
          <a href={exportStatusUrl}>
            <ViewCSVButton>
              {intl.formatMessage(messages.viewRequestCsvLinkText)}
            </ViewCSVButton>
          </a>
        )}
        {subscribe && renderDownloadButton()}
      </Space>
    )
  }

  return (
    <ExporterProcessingStatusStyled>
      <Space align='center'>
        <img
          src={exportHeaderImage}
          aria-label={intl.formatMessage(messages.title)}
        />

        <Header as='h5' gutter>
          {intl.formatMessage(messages.title)}
        </Header>
      </Space>

      <Text align='center' gutter>
        {intl.formatMessage(messages.description1)}
        <br />
        {exportStatusUrl &&
          intl.formatMessage(messages.description2, {
            link: <a href={exportStatusUrl}>Requested CSVs</a>,
          })}
      </Text>

      <Spacer space={1} />

      <table className='export-filters-table'>
        <tbody>
          {exportFilters.map((filter, index) => (
            <tr key={index}>
              <th>
                <Text bold>{filter.label}</Text>
              </th>
              <td>
                <Text>{filter.value}</Text>
              </td>
            </tr>
          ))}
          {subscribe && (
            <tr>
              <th>
                <Text bold id='export-status'>
                  {intl.formatMessage(messages.exportProgressLabel)}
                </Text>
              </th>
              <td aria-labelledby='export-status'>
                <Text
                  color={
                    processState === ExportStatuses.ERROR ? 'danger' : undefined
                  }
                >
                  {renderProcessStatus()}
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <Spacer space={2} />

      {renderProcessingStateButtons()}
    </ExporterProcessingStatusStyled>
  )
}

ExporterProcessingStatus.displayName = 'ExporterProcessingStatus'
